import { FileDownload } from "@mui/icons-material";
import { Box, IconButton, Slider, Stack, Typography } from "@mui/material";
import { AxiosError } from "axios";
import { enqueueSnackbar } from "notistack";
import React, { useMemo, useState } from "react";
import { Bar } from "react-chartjs-2";
import { useTranslation } from "react-i18next";

import { prepareMoneyFlowRequestDto } from "@/api/hooks/stat-analytics-hooks";
import { colorPool } from "@/module/service-configs-module/statistics-visualization-module/first-svetofor/util";
import { MoneyFlowRequestDto } from "@/shared/api";
import { useApi } from "@/shared/api/use-api";

type TimingChartProps = {
  action: string;
  street: string;
  data: number[];
  index: number;
  filters: MoneyFlowRequestDto | null;
  periodFilter: number;
};

const RANGE = 5;

export const TimingChart = ({
  data,
  index,
  action,
  street,
  filters,
  periodFilter,
}: TimingChartProps) => {
  const { statAnalyticsApi } = useApi();
  const [isMouseOver, setIsMouseOver] = useState(false);
  const { t } = useTranslation();
  const [slider, setSlider] = useState([0, RANGE]);
  const stepSize = useMemo(() => {
    switch (periodFilter) {
      case 1000:
        return 1;
      case 500:
        return 0.5;
      case 100:
        return 0.1;
    }
    return 0.1;
  }, [periodFilter]);

  const [labels, actualData] = useMemo(() => {
    const res: number[] = [];
    const dataRes: Array<{ x: number; y: number }> = [];
    let start = 0;
    data.forEach((item) => {
      res.push(start);
      start += stepSize;
      start = Number(start.toFixed(2));
      dataRes.push({
        x: start,
        y: item,
      });
    });

    return [res, dataRes];
  }, [data, stepSize]);

  const filteredData = useMemo(() => {
    return actualData.filter((item) => item.x >= slider[0] && item.x <= slider[1]);
  }, [slider, actualData]);

  const [isExportingLoading, setIsExportingLoading] = useState(false);

  const handleExportClick = async () => {
    if (isExportingLoading || !filters) {
      return;
    }

    try {
      setIsExportingLoading(true);
      const response = await statAnalyticsApi.exportTimings(
        {
          streets: [street],
          action_types: [action],
          query: prepareMoneyFlowRequestDto(filters),
        },
        {
          responseType: "blob",
        }
      );
      const contentDisposition = response?.headers["content-disposition"];
      if (contentDisposition) {
        const fileName = contentDisposition.split("filename=")[1].split(";")[0];
        const url = URL.createObjectURL(response.data as Blob);
        const a = document.createElement("a");
        a.style.display = "none";
        a.href = url;
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        a.remove();
      }
    } catch (e) {
      const message =
        (e as unknown as AxiosError<{ message: string }>)?.response?.data?.message || e;

      enqueueSnackbar(message as string, {
        variant: "error",
        autoHideDuration: 2000,
      });
    } finally {
      setIsExportingLoading(false);
    }
  };

  const title = `${street} - ${action}`;
  const smallTicks = slider[1] - slider[0] >= 10;

  return (
    <Stack
      sx={{ alignItems: "center", position: "relative", maxHeight: "90%" }}
      onMouseEnter={() => {
        setIsMouseOver(true);
      }}
      onMouseLeave={() => {
        setIsMouseOver(false);
      }}
    >
      {isMouseOver && (
        <IconButton
          loading={isExportingLoading}
          sx={{ position: "absolute", right: "8px", top: "-8px" }}
          onClick={handleExportClick}
        >
          <FileDownload />
        </IconButton>
      )}
      <Typography variant="body2" sx={{ fontSize: "16px" }}>
        {title}
      </Typography>
      <Box sx={{ pl: 2, pr: 2, width: "calc(100% - 32px)" }}>
        <Slider
          aria-label={title}
          defaultValue={0}
          step={1}
          value={slider}
          valueLabelDisplay="auto"
          onChange={(_e, newValue) => {
            const range = newValue as number[];
            setSlider(range);
          }}
          min={0}
          max={labels[labels.length - 1]}
          disableSwap
        />
      </Box>
      <Bar
        data={{
          labels,
          datasets: [
            {
              label: t("timings"),
              data: filteredData,
              backgroundColor: colorPool[index],
            },
          ],
        }}
        options={{
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: { display: false },
          },
          scales: {
            x: {
              type: "linear",
              ticks: {
                font: {
                  size: smallTicks ? 10 : 14, // Set the desired font size for X-axis labels
                },
                autoSkip: false,
                stepSize,
                callback: function (value) {
                  const val = Number(
                    parseInt(String((value as number) * 1000), 10).toFixed(0)
                  );
                  return val %
                    (periodFilter === 100 ? 500 : smallTicks ? 1000 : periodFilter) ===
                    0
                    ? parseFloat(String(value)).toFixed(periodFilter === 1000 ? 0 : 1)
                    : "";
                },
              },
            },
            y: {
              stacked: true,
              ticks: {
                callback: function (value) {
                  return value.toLocaleString(); // красиво отображать 100,000
                },
              },
            },
          },
        }}
      />
    </Stack>
  );
};
